import React, { useState } from "react";
import ReactModal from "react-modal";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

import ai from "../../../helpers/axios";
import { LoadingButton } from "../../shared/loading-button";
import getApiErrorMessage from "../../../helpers/get-api-error-message";

const COPY = {
  verify: {
    title: "Trip Verification",
    body: "Are you sure you want to verify this trip? This action cannot be undone, and only Admins will be able to edit this trip further.",
    cta: "Verify Trip",
    success: "Trip verified",
  },
  unverify: {
    title: "Unverify Trip",
    body: "You are about to remove the Verified status from this Trip. Once unverified, the Trip will become editable again and must be verified before it can be considered complete. Are you sure you want to continue?",
    cta: "Unverify Trip",
    success: "Trip unverified",
  },
  lock: {
    title: "Lock Trip",
    body: "Are you sure you want to lock this Trip? It will become read-only for all users except Super Admins.",
    cta: "Lock Trip",
    success: "Trip locked",
  },
  unlock: {
    title: "Unlock Trip",
    body: "Are you sure you want to unlock this Trip? The Trip Owner will be able to edit and verify it again.",
    cta: "Unlock Trip",
    success: "Trip unlocked",
  },
};

const LOCK_MODES = ["lock", "unlock"];

const VerificationModal = ({ isOpen, onClose, tripId, mode, onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [saving, setSaving] = useState(false);

  const copy = COPY[mode] || COPY.verify;
  const isLockMode = LOCK_MODES.includes(mode);
  const verified = mode === "verify";
  const locked = mode === "lock";

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();

      if (isLockMode) {
        await ai.auth(token).patch(`/api/trips/${tripId}/lock`, { locked });
      } else {
        await ai.auth(token).patch(`/api/trips/${tripId}/verify`, { verified });
      }

      toast.success(copy.success);
      if (onSaved) onSaved({ mode, verified, locked });
      onClose();
    } catch (error) {
      console.error(
        `Error updating trip ${isLockMode ? "lock" : "verification"}:`,
        error,
      );
      toast.error(
        getApiErrorMessage(
          error,
          `Failed to update trip ${isLockMode ? "lock" : "verification"}`,
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onClose}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgb(32 53 68 / 50%)",
        },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "554px",
          minWidth: "calc(min(500px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header">
        <h4>{copy.title}</h4>
      </div>

      <div className="modal-body width-100">
        <p className="mt-10 mb-10">{copy.body}</p>
      </div>

      <div className="modal-footer w-100 mt-10 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          isLoading={saving}
          onClick={handleConfirm}
        >
          {copy.cta}
        </LoadingButton>
        <button
          type="button"
          className="base-button btn-danger flex-half"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default VerificationModal;
